import { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext, apiClient } from '../App';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { 
  Plus, 
  Users, 
  MapPin, 
  ChevronRight,
  Loader2,
  ArrowLeft,
  Users2,
  Check,
  X
} from 'lucide-react';
import { toast } from 'sonner';
import Footer from '../components/Footer';

const MyClubs = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [clubs, setClubs] = useState([]);
  const [pendingInvites, setPendingInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actingInviteId, setActingInviteId] = useState(null);

  useEffect(() => {
    fetchClubs();
  }, []);

  const fetchClubs = async () => {
    try {
      setLoading(true);
      const [clubsResponse, invitesResponse] = await Promise.all([
        apiClient.get('/clubs/mine'),
        apiClient.get('/club-invites/mine'),
      ]);
      setClubs(clubsResponse.data.clubs || []);
      setPendingInvites(invitesResponse.data.invites || []);
    } catch (error) {
      console.error('Failed to load clubs:', error);
      toast.error('Failed to load your clubs');
      setClubs([]);
      setPendingInvites([]);
    } finally {
      setLoading(false);
    }
  };

  const handleInviteAction = async (inviteId, action) => {
    setActingInviteId(inviteId);
    try {
      await apiClient.post(`/club-invites/${inviteId}/${action}`);
      toast.success(action === 'accept' ? 'Club invite accepted' : 'Club invite declined');
      await fetchClubs();
    } catch (error) {
      toast.error(error.response?.data?.detail || `Could not ${action} invite`);
    } finally {
      setActingInviteId(null);
    }
  };

  const getRoleBadge = (club) => {
    const role = String(club.current_user_role || '').toLowerCase();
    if (club.is_owner || role === 'owner' || role === 'host') {
      return { label: 'Owner', className: 'bg-orange-100 text-orange-700' };
    }
    if (club.is_manager || role === 'manager' || role === 'coach') {
      return { label: 'Manager', className: 'bg-blue-100 text-blue-700' };
    }
    return { label: 'Member', className: 'bg-zinc-100 text-zinc-600' };
  };

  return (
    <div className="min-h-screen bg-zinc-50 flex flex-col">
      {/* Header */}
      <header
        className="mobile-page-header bg-white border-b border-zinc-200 sticky top-0 z-10"
        style={{ paddingTop: 'max(1rem, env(safe-area-inset-top))' }}
      >
        <div className="max-w-4xl mx-auto px-4 pb-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button 
                onClick={() => navigate('/dashboard')}
                className="mobile-page-back p-2 -ml-2 text-zinc-500 hover:text-zinc-900 transition-colors"
                aria-label="Back to dashboard"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div>
                <h1 className="text-xl font-bold text-zinc-900">My Clubs</h1>
                <p className="text-sm text-zinc-500">Manage your basketball crews</p>
              </div>
            </div>
            <Button
              onClick={() => navigate('/create-club')}
              className="bg-orange-500 hover:bg-orange-600"
              data-testid="create-club-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              New Club
            </Button>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="flex-1 max-w-4xl mx-auto w-full px-4 py-6">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
          </div>
        ) : clubs.length === 0 && pendingInvites.length === 0 ? (
          <Card className="p-8 text-center bg-gradient-to-br from-orange-50 to-amber-50 border-orange-200">
            <Users2 className="w-16 h-16 text-orange-400 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-zinc-900 mb-2">No clubs yet</h2>
            <p className="text-zinc-600 mb-6 max-w-md mx-auto">
              Create a Run Club to bring your basketball crew from GroupMe into Open Runs. 
              Share your migration link and grow your community.
            </p>
            <Button
              onClick={() => navigate('/create-club')}
              className="bg-orange-500 hover:bg-orange-600"
              data-testid="create-first-club-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Create Your First Club
            </Button>
          </Card>
        ) : (
          <div className="space-y-4">
            {pendingInvites.length > 0 && (
              <section className="space-y-3" data-testid="pending-club-invites">
                <h2 className="text-sm font-semibold text-zinc-500 uppercase tracking-wide">
                  Pending Invites
                </h2>
                {pendingInvites.map((invite) => {
                  const club = invite.club || {};
                  return (
                    <Card
                      key={invite.id}
                      className="p-4 border-amber-200 bg-amber-50/60"
                      data-testid={`my-club-invite-${invite.id}`}
                    >
                      <div className="flex items-center justify-between gap-3">
                        <div className="min-w-0">
                          <h3 className="font-semibold text-zinc-900 truncate">{club.name || 'Club invite'}</h3>
                          {club.location_label && (
                            <p className="text-sm text-zinc-500 flex items-center gap-1 mt-1">
                              <MapPin className="w-4 h-4" />
                              {club.location_label}
                            </p>
                          )}
                          <span className="inline-flex mt-2 text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-100 text-amber-700">
                            Pending
                          </span>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <Button
                            size="sm"
                            onClick={() => handleInviteAction(invite.id, 'accept')}
                            disabled={actingInviteId === invite.id}
                            className="bg-orange-500 hover:bg-orange-600 text-white"
                            data-testid={`accept-club-invite-${invite.id}`}
                          >
                            {actingInviteId === invite.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4 mr-1" />}
                            Accept
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleInviteAction(invite.id, 'decline')}
                            disabled={actingInviteId === invite.id}
                            data-testid={`decline-club-invite-${invite.id}`}
                          >
                            <X className="w-4 h-4 mr-1" />
                            Decline
                          </Button>
                        </div>
                      </div>
                    </Card>
                  );
                })}
              </section>
            )}

            {clubs.map((club) => {
              const roleBadge = getRoleBadge(club);
              return (
                <Card
                  key={club.id}
                  className="p-4 cursor-pointer hover:shadow-md transition-all hover:border-orange-300"
                  onClick={() => navigate(`/club/${club.slug}`)}
                  data-testid={`club-card-${club.slug}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <h3 className="font-semibold text-zinc-900 text-lg">{club.name}</h3>
                        <span className={`inline-flex text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded ${roleBadge.className}`}>
                          {roleBadge.label}
                        </span>
                      </div>
                      <p className="text-sm text-zinc-500 flex items-center gap-1 mt-1">
                        <MapPin className="w-4 h-4" />
                        {club.location_label}
                      </p>
                      <div className="mt-2 flex items-center gap-4 text-sm text-zinc-500">
                        <span className="flex items-center gap-1">
                          <Users className="w-4 h-4" />
                          {club.member_count || 1} member{(club.member_count || 1) !== 1 ? 's' : ''}
                        </span>
                      </div>
                    </div>
                    <ChevronRight className="w-5 h-5 text-zinc-400" />
                  </div>
                </Card>
              );
            })}
          </div>
        )}
      </main>

      <Footer />
    </div>
  );
};

export default MyClubs;
